import { SystemStyleObject } from '@chakra-ui/react'

import { tokenVar } from '../../theme/tokenVar'
import { ColorScheme } from '../../types'

const tokens = {
  dark: {
    bgOverlay: 'bg.overlay',
    bgPanel: 'bg.card-elevated',
    textPrimary: 'text.primary',
    sectionDivider: 'border.section-divider',
  },
  light: {
    bgOverlay: 'light.bg.overlay',
    bgPanel: 'light.bg.app',
    textPrimary: 'light.text.primary',
    sectionDivider: 'light.border.section-divider',
  },
}

/** Duration of the slide/scrim transition. The exit unmount waits on
 * `transitionend` with this (+ a margin) as the timeout fallback. */
export const DRAWER_TRANSITION_MS = 300

export const overlayStyles = (
  colorScheme: ColorScheme,
  zIndex = 1000,
  isVisible = false
): SystemStyleObject => ({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex,
  bg: tokens[colorScheme].bgOverlay,
  opacity: isVisible ? 1 : 0,
  transition: `opacity ${DRAWER_TRANSITION_MS}ms ease`,
})

export const panelStyles = (
  colorScheme: ColorScheme,
  width: string,
  isVisible = false
): SystemStyleObject => ({
  position: 'absolute',
  top: 0,
  right: 0,
  h: '100%',
  w: width,
  maxW: '100vw',
  display: 'flex',
  flexDirection: 'column',
  bg: tokens[colorScheme].bgPanel,
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
  outline: 'none',
  transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
  transition: `transform ${DRAWER_TRANSITION_MS}ms ease`,
})

export const headerStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
  px: '24px',
  minH: '65px',
  borderBottom: '1px solid',
  borderColor: tokens[colorScheme].sectionDivider,
  flexShrink: 0,
})

export const titleStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  fontFamily: "'Soehne Kraftig', sans-serif",
  fontSize: '20px',
  fontWeight: '500',
  lineHeight: '28px',
  letterSpacing: '-0.53px',
  color: tokens[colorScheme].textPrimary,
  flex: 1,
  minW: 0,
})

export const headerActionsStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
}

// Scrollbar thumb per scheme - the UA default scrollbar ignores the
// colorScheme entirely, so the scrollable body styles its own.
const SCROLLBAR_THUMB: Record<ColorScheme, string> = {
  dark: tokenVar('colors.border.strong'),
  light: tokenVar('colors.light.border.strong'),
}

export const bodyStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  flex: 1,
  overflowY: 'auto',
  px: '24px',
  py: '24px',
  color: tokens[colorScheme].textPrimary,
  fontFamily: "'Soehne', sans-serif",
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '24px',
  // Standard properties (Firefox, Chrome 121+); WebKit pseudos as fallback.
  scrollbarWidth: 'thin',
  scrollbarColor: `${SCROLLBAR_THUMB[colorScheme]} transparent`,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: SCROLLBAR_THUMB[colorScheme],
    borderRadius: '4px',
  },
})

export const footerStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '8px',
  px: '24px',
  py: '16px',
  borderTop: '1px solid',
  borderColor: tokens[colorScheme].sectionDivider,
  flexShrink: 0,
})
